import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";
import { MeHealthLogo, MeHealthClock, MeHealthFooter } from "react-lib/apps/CheckIn/CheckInLayout";
import "react-lib/apps/CheckIn/CheckInCommon.css";

import { DashboardHeader, DashboardFooter } from "./CardServicesQueueLayout";

// ======================= Types =======================
type QueueItem = {
  lab_order_id: number;
  encounter_id: number;
  patient_id: number;
  vn: string;
  is_called: boolean;
};

type CardServicesQueueProps = {
  onEvent: (e: any) => void;
  phlebotomyQueue?: {
    appointments: QueueItem[];
    walk_ins: QueueItem[];
  };
  readOnly?: boolean;
  // เมื่อ true → call API + refresh เท่านั้น ไม่ navigate ไป Specimen Collection
  skipNavigation?: boolean;
  // เมื่อ true → ใช้ header/footer style แบบ Dashboard (เหมือน CardQueueDoctor)
  dashboardStyle?: boolean;
  // Division label สำหรับ dashboardStyle (default "ห้องแลป")
  // เมื่อ true → สีตาม theme ของ HIS V4 (--hv4-*) แทนสีชมพูของ MeHealth
  themeColors?: boolean;
  divisionName?: string;
};

const POLL_INTERVAL = 10000;

type SectionColors = {
  label: string;
  border: string;
  calledBorder: string;
  bg: string;
};

const PALETTE_MEHEALTH: Record<"appointment" | "walkin", SectionColors> = {
  appointment: {
    label: "#F382A0",
    border: "#FFDBDB",
    calledBorder: "#91C561",
    bg: "#FFF7F7",
  },
  walkin: {
    label: "#5685B4",
    border: "#91B2D4",
    calledBorder: "#91B2D4",
    bg: "#F8FAFC",
  },
};

const PALETTE_DASHBOARD: Record<"appointment" | "walkin", SectionColors> = {
  appointment: {
    label: "rgb(29, 189, 212)",
    border: "#1DBDD4",
    calledBorder: "#A5E0EA",
    bg: "#E8F8FB",
  },
  walkin: {
    label: "#5685B4",
    border: "#5685B4",
    calledBorder: "#91B2D4",
    bg: "#F4F9FC",
  },
};

const PALETTE_THEME: Record<"appointment" | "walkin", SectionColors> = {
  appointment: {
    label: "var(--hv4-brand)",
    border: "var(--hv4-brand-line)",
    calledBorder: "var(--hv4-ok-line)",
    bg: "var(--hv4-brand-soft)",
  },
  walkin: {
    label: "var(--hv4-blue)",
    border: "var(--hv4-line)",
    calledBorder: "var(--hv4-line)",
    bg: "var(--hv4-bg-subtle)",
  },
};

// ======================= Styles =======================
const styles = {
  container: {
    height: "100%",
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.5rem 2rem 0.5rem 0",
    flexShrink: 0,
    borderBottom: "2px dashed #F382A0",
    position: "relative" as const,
  },
  logo: {
    height: "4.5rem !important" as any,
    width: "auto !important" as any,
    zIndex: 1,
  },
  title: {
    fontWeight: 600,
    fontSize: "2rem",
    color: "#3C3C3C",
    position: "absolute" as const,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1,
  },
  content: {
    flex: 1,
    overflow: "auto",
    padding: "1rem 2rem",
    display: "flex",
    flexDirection: "column" as const,
  },
  section: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
  },
  sectionLabel: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "1.5rem",
    marginBottom: "0.75rem",
  },
  sectionBox: {
    borderRadius: "1.25rem",
    border: "2px solid #EAECED",
    padding: "1.5rem",
    flex: 1,
    marginBottom: "0.75rem",
  },
  queueGrid: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "0.75rem",
  },
  queueCard: {
    width: "6rem",
    height: "4rem",
    borderRadius: "0.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: "2.5rem",
  },
  footer: {
    padding: "0.5rem 2rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexShrink: 0,
  },
  passedNote: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "1rem",
    color: "#5D8439",
  },
};

// ======================= Component =======================
const CardServicesQueue = (props: CardServicesQueueProps) => {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [time, setTime] = useState<string>(moment().format("HH:mm:ss"));
  const [date] = useState<string>(dateToStringWithoutTimeBE(new Date()));

  const fetchQueue = () => {
    props.onEvent({
      message: "GetPhlebotomyQueue",
      params: {},
    });
  };

  const handleCallQueue = (item: QueueItem) => {
    props.onEvent({
      message: "CallPhlebotomyQueue",
      params: {
        encounterId: item.encounter_id,
        labOrderId: item.lab_order_id,
        patientId: props.skipNavigation ? undefined : item.patient_id,
        queueType: 1,
        vn: item.vn,
        isCalled: item.is_called,
      },
    });
  };

  useEffect(() => {
    fetchQueue();

    timerRef.current = setInterval(fetchQueue, POLL_INTERVAL);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!props.dashboardStyle) return;

    const id = setInterval(() => setTime(moment().format("HH:mm:ss")), 1000);

    return () => clearInterval(id);
  }, [props.dashboardStyle]);

  const appointments = props.phlebotomyQueue?.appointments || [];
  const walkIns = props.phlebotomyQueue?.walk_ins || [];

  const passedCount = [...appointments, ...walkIns].filter((i) => i.is_called).length;

  const palette = props.dashboardStyle
    ? PALETTE_DASHBOARD
    : props.themeColors
      ? PALETTE_THEME
      : PALETTE_MEHEALTH;

  const getQueueCardStyle = (item: QueueItem, type: "appointment" | "walkin") => {
    const color = palette[type];
    if (item.is_called) {
      return {
        ...styles.queueCard,
        border: `1px solid ${color.calledBorder}`,
        color: "#5D8439",
        background: "#FFFFFF",
      };
    }
    return {
      ...styles.queueCard,
      border: `2px solid ${color.border}`,
      color: "#000000",
      background: "#FFFFFF",
    };
  };

  const queueContent = (
    <div style={styles.content}>
      {/* Appointment Section */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>
          <span style={{ fontSize: "1.5rem" }}>📅</span>
          <span style={{ color: palette.appointment.label }}>นัดหมาย (Appointment)</span>
        </div>
        <div style={{ ...styles.sectionBox, background: palette.appointment.bg }}>
          <div style={styles.queueGrid}>
            {appointments.map((item) => (
              <div
                key={item.lab_order_id}
                style={{
                  ...getQueueCardStyle(item, "appointment"),
                  cursor: props.readOnly ? "default" : "pointer",
                }}
                onClick={props.readOnly ? undefined : () => handleCallQueue(item)}
              >
                {item.vn}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Walk-in Section */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>
          <span style={{ fontSize: "1.5rem" }}>🚶</span>
          <span style={{ color: palette.walkin.label }}>ไม่ได้นัดหมาย (Walk-in)</span>
        </div>
        <div style={{ ...styles.sectionBox, background: palette.walkin.bg }}>
          <div style={styles.queueGrid}>
            {walkIns.map((item) => (
              <div
                key={item.lab_order_id}
                style={{
                  ...getQueueCardStyle(item, "walkin"),
                  cursor: props.readOnly ? "default" : "pointer",
                }}
                onClick={props.readOnly ? undefined : () => handleCallQueue(item)}
              >
                {item.vn}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (props.dashboardStyle) {
    return (
      <div style={styles.container}>
        <DashboardHeader
          title="คิวห้องเจาะเลือด"
          divisionName={props.divisionName}
          date={date}
          time={time}
        />

        {queueContent}

        <DashboardFooter passedCount={passedCount} />
      </div>
    );
  }

  // MeHealth-style (default)
  return (
    <div style={styles.container}>
      {/* Header */}
      <div
        style={{
          ...styles.header,
          ...(props.themeColors && { borderBottom: "2px dashed var(--hv4-brand)" }),
        }}
      >
        <MeHealthLogo style={styles.logo} />
        <div style={styles.title}>คิวเจาะเลือด (Phlebotomy Services)</div>
        <MeHealthClock />
      </div>

      {queueContent}

      {/* Footer */}
      <div style={styles.footer}>
        <div style={styles.passedNote}>
          <div
            style={{ width: "1rem", height: "1rem", borderRadius: "50%", background: "#5D8439" }}
          />
          ** คิวรับบริการที่ถูกเรียกไปแล้ว (Passed Queue)**
        </div>
        <MeHealthFooter
          style={{ padding: 0, ...(props.themeColors && { color: "var(--hv4-brand)" }) }}
        />
      </div>
    </div>
  );
};

CardServicesQueue.displayName = "CardServicesQueue";

export default CardServicesQueue;
